import { Alert, CircularProgress } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useParams } from "react-router";

import { useNavigation } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { PageLayout } from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { useAgentThread } from "@/shared/domain/agents/hooks";
import {
  ThreadVisibility,
  type AgentThreadRecord,
} from "@/shared/domain/agentThread";
import { RobotoDomainException } from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./ForkThreadRedirect.module.css";

/**
 * Redirect route at ``/agents/threads/:threadId/fork``.
 *
 * Navigating here forks the encoded thread (the whole conversation, up to
 * and including its last message) into a new caller-owned thread, then
 * replaces the URL with that fork's detail page. The user never dwells on
 * this URL — it exists so a thread can be forked from a plain link
 * (shared by a teammate, a bookmark, etc.) without first opening the
 * source thread's read-only viewer and clicking the per-message fork
 * button.
 *
 * On success a notification tells the user the fork is private to them,
 * since that is the non-obvious consequence of landing on a brand-new
 * thread that looks just like the one they came from.
 *
 * Loading and error states flow through ``<React.Suspense>`` and
 * ``<ErrorBoundary>`` — the thread fetch lives inside
 * :func:`ForkThreadRedirectContent`, which suspends until the source
 * thread is available (the fork point is its last message). A failed
 * fork (e.g. the caller lacks permission) surfaces the server's message
 * for 4xx and a generic fallback otherwise, mirroring
 * :class:`AgentThreadDetail`.
 */
export function ForkThreadRedirect() {
  const { threadId } = useParams<{ threadId: string }>();

  if (threadId === undefined) {
    // Programming error — the route declaration registers
    // ``/agents/threads/:threadId/fork`` so React Router guarantees the
    // param is set by the time this component mounts.
    throw new Error("threadId not provided");
  }

  return (
    <PageLayout title={`Roboto - Forking thread - ${threadId}`}>
      <ErrorBoundary fallback={({ error }) => <ForkErrorAlert error={error} />}>
        <React.Suspense
          fallback={<ForkStatus message="Loading thread to fork…" />}
        >
          <ForkThreadRedirectContent threadId={threadId} />
        </React.Suspense>
      </ErrorBoundary>
    </PageLayout>
  );
}

function ForkStatus({ message }: { message: string }) {
  return (
    <div className={styles.statusContainer}>
      <CircularProgress size="1.5rem" />
      <span className={styles.statusMessage}>{message}</span>
    </div>
  );
}

function ForkErrorAlert({ error }: { error: unknown }) {
  // 4xx errors are user-actionable (thread not found, forbidden, the
  // thread has no messages to fork from, etc.) — surface the server's
  // message verbatim. Anything else gets the generic copy so we don't
  // leak server-side details and the user has a contact path. Mirrors
  // ``AgentThreadErrorAlert`` in ``AgentThreadDetail``.
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  return (
    <Alert severity="error">
      Failed to fork this thread. Please contact{" "}
      <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this problem
      persists.
    </Alert>
  );
}

function ForkThreadRedirectContent({ threadId }: { threadId: string }) {
  const { goto } = useNavigation();
  const showNotification = useNotification();
  const { agentThreadService } = useDomainServices();
  const queryClient = useQueryClient();
  const { data: thread } = useAgentThread({ threadId });

  // The fork mutation must fire exactly once. ``React.StrictMode``
  // double-invokes effects in development, and a re-render (e.g. from the
  // mutation's own state transitions) would otherwise re-enter the
  // effect — both would create duplicate forks. Guard with a ref that
  // survives re-renders.
  const startedRef = React.useRef(false);

  const hasMessages = thread.messages.length > 0;

  const fork = useMutation<AgentThreadRecord, Error, void>({
    mutationFn: () => {
      // Fork the whole conversation: the fork point is the last message's
      // sequence number, which is its index in ``messages`` (inclusive).
      // The empty-thread case is guarded before the mutation is started.
      const lastMessageSequenceNum = thread.messages.length - 1;
      return agentThreadService.forkAgentThread(
        threadId,
        lastMessageSequenceNum,
        { orgId: thread.org_id },
      );
    },
    onSuccess: async (forked) => {
      // Invalidate the user's thread-list/history queries so the new fork
      // appears alongside their other threads. Same key the per-message
      // fork button invalidates.
      await queryClient.invalidateQueries({
        queryKey: ["agentThreadsForUser"],
      });
      // The fork is caller-owned and the backend defaults new threads to
      // PRIVATE. Tell the user it's private only when it actually is, so
      // the message can't make a false privacy promise if that default
      // ever changes.
      if (forked.visibility === ThreadVisibility.PRIVATE) {
        showNotification(
          "Created a private fork. This copy is private to you.",
          "info",
          8000,
          "accent",
        );
      } else {
        showNotification("Created your own fork of this thread.", "info", 8000);
      }
      // Replace rather than push so the back button returns to wherever
      // the user came from, not to this redirect (which would re-fork).
      goto.agentThread({ threadId: forked.thread_id }, { replace: true });
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error, {
        context: { threadId },
      });
    },
  });

  // ``mutate`` is referentially stable across renders (TanStack Query
  // guarantees it), unlike the ``fork`` object whose identity changes on
  // every mutation state transition. Depend on the destructured function so
  // the effect doesn't re-run on each transition — and so exhaustive-deps
  // doesn't demand the whole churning object.
  const { mutate: startFork } = fork;

  React.useEffect(() => {
    // The empty-thread case is handled by the render-phase throw below;
    // skip it here so we never POST a guaranteed-invalid (negative)
    // sequence number.
    if (startedRef.current || !hasMessages) {
      return;
    }
    startedRef.current = true;
    startFork();
  }, [startFork, hasMessages]);

  if (!hasMessages) {
    // Nothing to fork. Throw during render (not in the effect — effect
    // throws escape the ErrorBoundary) so ``ForkErrorAlert`` surfaces it
    // the same way a 4xx from the server would. ``error_code: "400"``
    // makes ``isClientError()`` true, so the message renders verbatim.
    throw new RobotoDomainException({
      error_code: "400",
      message: "This thread has no messages to fork.",
    });
  }

  if (fork.isError) {
    return <ForkErrorAlert error={fork.error} />;
  }

  // Neutral wording: the fork's visibility isn't known until the POST
  // returns, so the in-flight message must not claim "private" — the
  // success notification makes that promise only when it's actually true.
  return <ForkStatus message="Creating your fork…" />;
}
